// The scatter configs (which props grow on which biomes, how dense) and the
// per-prop tables that go with them. Split out of scatterInstances.ts (which
// re-exports everything here).

import type { ScatterConfig, ScatterPropType } from './TerrainScatterMaterials.js';

/**
 * Contact-shadow diameter per prop type (world units). Absent = no blob
 * (grass stays shadow-free — it's lying on the ground already).
 */
export const SHADOW_RADIUS: Partial<Record<ScatterPropType, number>> = {
  pine: 0.3,
  spruce: 0.28,
  oak: 0.34,
  birch: 0.24,
  bush: 0.22,
  rock: 0.16,
  cactus: 0.14,
  palm: 0.22,
  jungleTree: 0.4,
  snowBoulder: 0.2,
};

// --- Scatter configs ---

const TREE_BIOME_MIX = {
  forest: [
    { propType: 'pine', weight: 4 },
    { propType: 'spruce', weight: 2 },
    { propType: 'oak', weight: 2 },
    { propType: 'birch', weight: 1 },
    { propType: 'bush', weight: 2 },
  ],
  highland: [
    { propType: 'spruce', weight: 3 },
    { propType: 'pine', weight: 2 },
    { propType: 'bush', weight: 1 },
  ],
  jungle: [
    { propType: 'jungleTree', weight: 5 },
    { propType: 'palm', weight: 3 },
  ],
  lowland: [
    { propType: 'oak', weight: 3 },
    { propType: 'birch', weight: 2 },
    { propType: 'bush', weight: 3 },
  ],
} as const;

export const SCATTER_CONFIGS: ScatterConfig[] = [
  {
    id: 'forest-trees',
    biomes: ['forest'],
    props: TREE_BIOME_MIX.forest,
    minCount: 4,
    maxCount: 9,
    castShadow: true,
    yOffset: 0,
    exceptClimates: ['tropical'],
  },
  {
    // Hot and wet: the forest tiles of a tropical map are jungle (broad canopies and palms),
    // thicker than the temperate wood, with ferns on the floor.
    id: 'jungle-trees',
    biomes: ['forest'],
    props: TREE_BIOME_MIX.jungle,
    minCount: 5,
    maxCount: 10,
    castShadow: true,
    yOffset: 0,
    climates: ['tropical'],
  },
  {
    id: 'jungle-ferns',
    biomes: ['forest'],
    props: [{ propType: 'fern', weight: 1 }],
    minCount: 6,
    maxCount: 14,
    castShadow: false,
    yOffset: 0,
    patchy: true,
    climates: ['tropical'],
  },
  {
    id: 'highland-trees',
    biomes: ['hills', 'tundra'],
    props: TREE_BIOME_MIX.highland,
    minCount: 1,
    maxCount: 3,
    castShadow: true,
    yOffset: 0,
  },
  {
    id: 'lowland-trees',
    biomes: ['plains'],
    props: TREE_BIOME_MIX.lowland,
    minCount: 0,
    maxCount: 3,
    castShadow: true,
    yOffset: 0,
  },
  {
    id: 'rocks',
    biomes: ['mountain', 'hills', 'tundra', 'desert'],
    props: [{ propType: 'rock', weight: 1 }],
    minCount: 1,
    maxCount: 5,
    castShadow: true,
    yOffset: 0,
  },
  {
    // Small rocks on the soft biomes (the big `rocks` config covers the
    // harsh ones) — sparse, half-scale.
    id: 'pebbles',
    biomes: ['plains', 'forest'],
    props: [{ propType: 'rock', weight: 1 }],
    minCount: 0,
    maxCount: 2,
    castShadow: true,
    yOffset: 0,
    scale: 0.5,
  },
  {
    // Flower patches: clustered like grass tufts, pastel blossoms whose
    // colour walks by grove (whole patches read white/yellow/pink). They
    // hide in winter (hideInWinter).
    id: 'flowers',
    biomes: ['plains'],
    props: [{ propType: 'flower', weight: 1 }],
    minCount: 0,
    maxCount: 8,
    castShadow: false,
    yOffset: 0,
    patchy: true,
    perInstanceColor: true,
    hideInWinter: true,
  },
  {
    id: 'grass',
    biomes: ['plains', 'forest', 'hills'],
    props: [{ propType: 'grass', weight: 1 }],
    minCount: 10,
    maxCount: 22,
    castShadow: false,
    yOffset: 0,
    patchy: true,
  },
  {
    id: 'mountain-boulders',
    biomes: ['mountain'],
    props: [{ propType: 'rock', weight: 1 }],
    minCount: 0,
    maxCount: 3,
    castShadow: true,
    yOffset: 0,
    scale: 2.4,
  },
  {
    id: 'cacti',
    biomes: ['desert'],
    props: [{ propType: 'cactus', weight: 1 }],
    minCount: 1,
    maxCount: 4,
    castShadow: false,
    yOffset: 0,
  },
  {
    id: 'snow-boulders',
    biomes: ['mountain'],
    props: [{ propType: 'snowBoulder', weight: 1 }],
    minCount: 0,
    maxCount: 3,
    castShadow: false,
    yOffset: 0,
  },
];

export const TREES = new Set<ScatterPropType>([
  'pine',
  'spruce',
  'oak',
  'birch',
  'bush',
  'palm',
  'jungleTree',
]);

/** Whether a scatter config is drawn on a map of this climate (default temperate). */
export function configAppliesToClimate(config: ScatterConfig, climate = 'temperate'): boolean {
  if (config.climates && !config.climates.includes(climate)) return false;
  if (config.exceptClimates?.includes(climate)) return false;
  return true;
}
